import React, { useMemo } from "react";
import { 
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, 
  PieChart, Pie, RadialBarChart, RadialBar, Legend
} from "recharts";
import { cn } from "@/lib/utils";

interface RankingItem {
  id: string;
  label: string;
}

interface RankingVisualizationProps {
  items: RankingItem[];
  orderedIds?: string[];
  className?: string;
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function RankingVisualization({ items, orderedIds, className }: RankingVisualizationProps) {
  const chartData = useMemo(() => {
    // Use orderedIds if available, otherwise use items order
    const orderedItems = orderedIds 
      ? orderedIds.map(id => items.find(i => i.id === id)).filter(Boolean) as RankingItem[]
      : items;
    
    // Higher rank = higher value (reverse the index)
    return orderedItems.map((item, index) => ({
      name: item.label.length > 20 ? item.label.substring(0, 20) + '...' : item.label,
      fullName: item.label,
      value: orderedItems.length - index,
      fill: COLORS[index % COLORS.length],
    }));
  }, [items, orderedIds]);

  if (chartData.length === 0) {
    return (
      <div className={cn("h-64 flex items-center justify-center text-muted-foreground", className)}>
        Keine Prioritäten festgelegt
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 50)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis type="number" hide />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={120}
            tick={{ fontSize: 12 }}
          />
          <Bar 
            dataKey="value" 
            radius={[0, 4, 4, 0]}
            animationDuration={800}
            animationBegin={0}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Ranking List */}
      <div className="space-y-2">
        {chartData.map((item, index) => (
          <div 
            key={index}
            className="flex items-center gap-3 p-2 rounded-lg transition-all"
            style={{ 
              background: `linear-gradient(90deg, ${item.fill}20 0%, transparent ${100 - (chartData.length - index) * 20}%)` 
            }}
          >
            <span 
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: item.fill }}
            >
              {index + 1}
            </span>
            <span className="text-sm">{item.fullName}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
